import { createHmac } from "crypto";
import { USD } from "@dinero.js/currencies";
import { dinero } from "dinero.js";
import { z } from "zod";
import { BaseConnector } from "../baseConnector";
export class TMEConnector extends BaseConnector {
    getApiToken;
    getAppSecret;
    constructor({ getApiToken, getAppSecret }) {
        super();
        this.getApiToken = getApiToken;
        this.getAppSecret = getAppSecret;
    }
    get baseUrl() {
        return "https://api.tme.eu";
    }
    async search(params) {
        let response;
        if ("mpn" in params) {
            response = await this.searchByKeyword(params.mpn, params.manufacturer);
        }
        else if ("keyword" in params) {
            response = await this.searchByKeyword(params.keyword, params.manufacturer);
        }
        else {
            throw new Error("Invalid search params");
        }
        return this.processSearchResponse(response, params.manufacturer, true);
    }
    /**
     * Search for products by keyword/MPN
     * Implements rate limiting handling with exponential backoff
     */
    async searchByKeyword(keyword, _manufacturer) {
        // Create parameters object directly instead of using URLSearchParams
        const params = {
            Token: this.getApiToken(),
            Country: "US",
            Language: "EN",
            SearchPlain: keyword,
            SearchWithStock: String(true),
        };
        const signature = this.generateSignature(params, "Search");
        params.ApiSignature = signature;
        const formData = new URLSearchParams();
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        Object.entries(params).forEach(([key, value]) => {
            formData.append(key, value);
        });
        const maxRetries = 3;
        let retryCount = 0;
        let _lastError;
        while (retryCount < maxRetries) {
            try {
                const res = await this.post("Products/Search.json", formData.toString(), {
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                    },
                });
                return TMESearchResponseSchema.parse(res.payload);
            }
            catch (error) {
                _lastError = error;
                if (error instanceof Error &&
                    "status" in error &&
                    typeof error.status === "number" &&
                    error.status === 429) {
                    const delay = 1000 * Math.pow(2, retryCount);
                    await new Promise((resolve) => setTimeout(resolve, delay));
                    retryCount++;
                }
                else {
                    throw error;
                }
            }
        }
        return {
            Status: "ERROR",
            Data: {
                ProductList: [],
            },
        };
    }
    /**
     * Get prices and stock information for a list of symbols
     * Implements rate limiting handling with exponential backoff
     */
    async getPricesAndStocks(symbols) {
        if (symbols.length === 0) {
            return { Status: "OK", Data: { ProductList: [] } };
        }
        try {
            // Create parameters object directly instead of using URLSearchParams
            const params = {
                Token: this.getApiToken(),
                Country: "US",
                Language: "EN",
                Currency: "USD",
            };
            // Add symbols as array parameters
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            symbols.forEach((symbol, index) => {
                params[`SymbolList[${index}]`] = symbol;
            });
            const signature = this.generateSignature(params, "GetPrices");
            params.ApiSignature = signature;
            const formData = new URLSearchParams();
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            Object.entries(params).forEach(([key, value]) => {
                formData.append(key, value);
            });
            const maxRetries = 3;
            let retryCount = 0;
            let _lastError;
            while (retryCount < maxRetries) {
                try {
                    const res = await this.post("Products/GetPrices.json", formData.toString(), {
                        headers: {
                            "Content-Type": "application/x-www-form-urlencoded",
                        },
                    });
                    const response = TMEPriceResponseSchema.parse(res.payload);
                    if (response.Status === "OK") {
                        if (response.Data?.ProductList?.length) {
                            return response;
                        }
                        else if (response.Data?.PriceList?.length) {
                            return {
                                Status: "OK",
                                Data: {
                                    ProductList: [
                                        {
                                            Symbol: symbols[0] ?? "",
                                            PriceList: response.Data.PriceList,
                                        },
                                    ],
                                },
                            };
                        }
                    }
                    return { Status: response.Status, Data: { ProductList: [] } };
                }
                catch (error) {
                    _lastError = error;
                    if (error instanceof Error &&
                        "status" in error &&
                        typeof error.status === "number" &&
                        error.status === 429) {
                        const delay = 1000 * Math.pow(2, retryCount);
                        await new Promise((resolve) => setTimeout(resolve, delay));
                        retryCount++;
                    }
                    else {
                        throw error;
                    }
                }
            }
            return { Status: "ERROR", Data: { ProductList: [] } };
        }
        catch (error) {
            return { Status: "ERROR", Data: { ProductList: [] } };
        }
    }
    /**
     * Get delivery time information for a list of symbols
     * Implements rate limiting handling with exponential backoff
     */
    async getDeliveryTime(symbols) {
        if (symbols.length === 0) {
            return { Status: "OK", Data: { ProductList: [] } };
        }
        try {
            // Create parameters object directly instead of using URLSearchParams
            const params = {
                Token: this.getApiToken(),
                Country: "US",
                Language: "EN",
            };
            // Add symbols and amounts as array parameters
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            symbols.forEach((symbol, index) => {
                params[`SymbolList[${index}]`] = symbol;
                params[`AmountList[${index}]`] = "1"; // Default quantity of 1
            });
            const signature = this.generateSignature(params, "GetDeliveryTime");
            params.ApiSignature = signature;
            const formData = new URLSearchParams();
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            Object.entries(params).forEach(([key, value]) => {
                formData.append(key, value);
            });
            const maxRetries = 3;
            let retryCount = 0;
            let _lastError;
            while (retryCount < maxRetries) {
                try {
                    const res = await this.post("Products/GetDeliveryTime.json", formData.toString(), {
                        headers: {
                            "Content-Type": "application/x-www-form-urlencoded",
                        },
                    });
                    const response = TMEDeliveryResponseSchema.parse(res.payload);
                    if (response.Status === "OK") {
                        if (response.Data?.ProductList?.length) {
                            return {
                                Status: "OK",
                                Data: {
                                    ProductList: response.Data.ProductList.map((product) => {
                                        let deliveryTime = product.DeliveryTime;
                                        if (product.DeliveryList?.length) {
                                            const deliveryInfo = product.DeliveryList[0];
                                            if (deliveryInfo?.Status === "DS_AVAILABLE_IN_STOCK") {
                                                deliveryTime = 1;
                                            }
                                            else if (deliveryInfo?.Week) {
                                                deliveryTime = deliveryInfo.Week * 7;
                                            }
                                        }
                                        return {
                                            Symbol: product.Symbol,
                                            DeliveryTime: deliveryTime,
                                        };
                                    }),
                                },
                            };
                        }
                        else if (response.Data?.DeliveryTime !== undefined) {
                            return {
                                Status: "OK",
                                Data: {
                                    ProductList: [
                                        {
                                            Symbol: symbols[0] ?? "",
                                            DeliveryTime: response.Data.DeliveryTime,
                                        },
                                    ],
                                },
                            };
                        }
                    }
                    return { Status: response.Status, Data: { ProductList: [] } };
                }
                catch (error) {
                    _lastError = error;
                    if (error instanceof Error &&
                        "status" in error &&
                        typeof error.status === "number" &&
                        error.status === 429) {
                        const delay = 1000 * Math.pow(2, retryCount);
                        await new Promise((resolve) => setTimeout(resolve, delay));
                        retryCount++;
                    }
                    else {
                        throw error;
                    }
                }
            }
            return { Status: "ERROR", Data: { ProductList: [] } };
        }
        catch (error) {
            return { Status: "ERROR", Data: { ProductList: [] } };
        }
    }
    /**
     * Generates HMAC-SHA1 signature for TME API requests
     *
     * Implementation based on the official TME API PHP example:
     * https://github.com/tme-dev/TME-API/blob/master/PHP/basic/using_curl.php
     *
     * The signature is generated based on the OAuth 1.0a specification:
     * 1. Sort parameters alphabetically
     * 2. Build query string with PHP_QUERY_RFC3986 encoding
     * 3. Create signature base: POST&encoded_url&encoded_params
     * 4. Generate HMAC-SHA1 signature using the app secret
     */
    generateSignature(params, endpoint = "Search") {
        const paramString = this.phpHttpBuildQuery(params);
        // Create the API URL
        const apiUrl = `https://api.tme.eu/Products/${endpoint}.json`;
        // Create the signature base string exactly as in PHP example
        const signatureBase = `POST&${this.phpRawUrlEncode(apiUrl)}&${this.phpRawUrlEncode(paramString)}`;
        const hmac = createHmac("sha1", this.getAppSecret());
        hmac.update(signatureBase);
        return hmac.digest("base64");
    }
    /**
     * PHP-compatible rawurlencode implementation
     * Mimics PHP's rawurlencode function which is slightly different from JavaScript's encodeURIComponent
     * This matches PHP's rawurlencode exactly as used in TME's API examples
     */
    phpRawUrlEncode(str) {
        return encodeURIComponent(str)
            .replace(/!/g, "%21")
            .replace(/'/g, "%27")
            .replace(/\(/g, "%28")
            .replace(/\)/g, "%29")
            .replace(/\*/g, "%2A")
            .replace(/%7E/g, "~"); // PHP doesn't encode tilde
    }
    /**
     * PHP-compatible http_build_query implementation
     * Mimics PHP's http_build_query with PHP_QUERY_RFC3986 flag
     * This is critical for TME API signature generation to match their PHP examples
     */
    phpHttpBuildQuery(params) {
        const sortedKeys = Object.keys(params).sort();
        return sortedKeys
            .map((key) => {
            const encodedKey = this.phpRawUrlEncode(key);
            const encodedValue = this.phpRawUrlEncode(params[key] || "");
            return `${encodedKey}=${encodedValue}`;
        })
            .join("&");
    }
    async processSearchResponse(response, manufacturer, includePricing = false) {
        if (response.Status !== "OK" || !response.Data?.ProductList) {
            return [];
        }
        const filteredProducts = response.Data.ProductList.filter((product) => {
            if (manufacturer) {
                return product.Producer.toLowerCase().includes(manufacturer.toLowerCase());
            }
            return true;
        });
        let priceData = null;
        let deliveryData = null;
        if (includePricing && filteredProducts.length > 0) {
            const symbols = filteredProducts.map((p) => p.Symbol);
            try {
                [priceData, deliveryData] = await Promise.all([
                    this.getPricesAndStocks(symbols),
                    this.getDeliveryTime(symbols),
                ]);
            }
            catch (error) {
                priceData = null;
                deliveryData = null;
            }
        }
        return filteredProducts.map((product) => {
            let availableQuantity = 0;
            if (product.Packing && product.Packing.length > 0) {
                availableQuantity = product.Packing.reduce((sum, pack) => sum + (pack.Amount || 0), 0);
            }
            const priceBreaks = [];
            const priceProduct = priceData?.Data?.ProductList?.find((p) => p.Symbol === product.Symbol);
            if (priceProduct?.PriceList) {
                priceBreaks.push(...priceProduct.PriceList.map((pb) => ({
                    quantity: pb.Amount,
                    price: dinero({ amount: Math.round(pb.PriceValue * 100000), scale: 5, currency: USD }),
                    currency: "USD",
                })));
            }
            const deliveryProduct = deliveryData?.Data?.ProductList?.find((p) => p.Symbol === product.Symbol);
            const leadTimeDays = deliveryProduct?.DeliveryTime || null;
            return {
                distributor: "tme",
                mpn: product.OriginalSymbol || product.Symbol,
                dpn: product.Symbol,
                availableQuantity,
                minimumOrderQuantity: product.MinAmount || 1,
                datasheetUrl: product.DataSheetUrl || null,
                description: product.Description || "",
                leadTimeDays,
                lifecycleStatus: null,
                manufacturerName: product.Producer,
                packagingOptions: product.Unit ? [product.Unit] : [],
                priceBreaks,
                listingUrl: `https://www.tme.com/us/en-us/katalog/?queryPhrase=${encodeURIComponent(product.Symbol)}`,
                distributorAttributes: {
                    distributor: "tme",
                    Category: product.Category || product.CategoryName || "",
                },
            };
        });
    }
}
const TMEPackingSchema = z.object({
    Id: z.string().optional(),
    IdUnit: z.string().optional(),
    PackingTranslation: z.string().nullable().optional(),
    Amount: z.number().optional(),
});
const TMEProductSchema = z.object({
    Symbol: z.string(),
    OriginalSymbol: z.string().optional(),
    Producer: z.string(),
    Description: z.string().optional(),
    Category: z.string().optional(),
    CategoryName: z.string().optional(),
    InStock: z.number().optional(),
    MinAmount: z.number().optional(),
    Unit: z.string().optional(),
    DataSheetUrl: z.string().optional(),
    Packing: z.array(TMEPackingSchema).optional(),
});
const TMESearchResponseSchema = z.object({
    Status: z.string(),
    Data: z
        .object({
        ProductList: z.array(TMEProductSchema).optional(),
        Amount: z.number().optional(),
    })
        .optional(),
});
const TMEPriceBreakSchema = z.object({
    Amount: z.number(),
    PriceValue: z.number(),
});
const TMEPriceProductSchema = z.object({
    Symbol: z.string(),
    PriceList: z.array(TMEPriceBreakSchema).optional(),
});
const TMEPriceResponseSchema = z.object({
    Status: z.string(),
    Data: z
        .object({
        ProductList: z.array(TMEPriceProductSchema).optional(),
        PriceList: z.array(TMEPriceBreakSchema).optional(),
        DeliveryTime: z.number().optional(),
    })
        .optional(),
    ErrorCode: z.number().optional(),
    ErrorMessage: z.string().optional(),
    Error: z.unknown().optional(),
});
const TMEDeliveryInfoSchema = z.object({
    Amount: z.number().optional(),
    Status: z.string().optional(),
    Week: z.number().nullable().optional(),
});
const TMEDeliveryProductSchema = z.object({
    Symbol: z.string(),
    DeliveryTime: z.number().optional(),
    DeliveryList: z.array(TMEDeliveryInfoSchema).optional(),
});
const TMEDeliveryResponseSchema = z.object({
    Status: z.string(),
    Data: z
        .object({
        ProductList: z.array(TMEDeliveryProductSchema).optional(),
        DeliveryTime: z.number().optional(),
    })
        .optional(),
    ErrorCode: z.number().optional(),
    ErrorMessage: z.string().optional(),
    Error: z.unknown().optional(),
});
//# sourceMappingURL=tme.js.map